import { useState, useEffect } from 'react';
import {
  CreditCardIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowTopRightOnSquareIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { useAgency } from '../context/AgencyContext';
import {
  getAgencySubscription,
  createEmbeddedCheckoutSession,
  createBillingPortalSession,
  SubscriptionInfo,
} from '../services/api';
import EmbeddedCheckoutModal from '../components/EmbeddedCheckoutModal';

// Single subscription plan configuration
const AGENCY_PLAN = {
  id: 'agency',
  name: 'Agency Plan',
  priceCents: 3900, // $39/month
  features: [
    'Unlimited clients',
    'AI-powered voice receptionist',
    'Real-time analytics',
    'Stripe Connect for client billing',
    'Usage-based pricing on calls',
  ],
};

function formatPrice(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

function formatDate(dateString: string | null): string {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function getStatusBadge(status: string) {
  switch (status) {
    case 'active':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-sm font-medium text-green-800">
          <CheckCircleIcon className="h-4 w-4" />
          Active
        </span>
      );
    case 'trialing':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-0.5 text-sm font-medium text-blue-800">
          Trial
        </span>
      );
    case 'past_due':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2.5 py-0.5 text-sm font-medium text-yellow-800">
          <XCircleIcon className="h-4 w-4" />
          Past Due
        </span>
      );
    case 'canceled':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-sm font-medium text-red-800">
          <XCircleIcon className="h-4 w-4" />
          Canceled
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-sm font-medium text-gray-800">
          {status}
        </span>
      );
  }
}

export default function BillingPage() {
  const { currentAgency, loading: agencyLoading } = useAgency();
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  useEffect(() => {
    if (!currentAgency) return;

    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const subData = await getAgencySubscription(currentAgency!.id);
        setSubscription(subData);
      } catch (err: any) {
        setError(err.message || 'Failed to load billing data');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [currentAgency]);

  const handleSubscribe = async () => {
    if (!currentAgency) return;

    setActionLoading('subscribe');
    setError(null);
    try {
      const baseUrl = window.location.origin;
      const { clientSecret } = await createEmbeddedCheckoutSession(
        currentAgency.id,
        'agency',
        `${baseUrl}/checkout/return`
      );
      setClientSecret(clientSecret);
      setCheckoutOpen(true);
    } catch (err: any) {
      setError(err.message || 'Failed to create checkout session');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCheckoutClose = () => {
    setCheckoutOpen(false);
    setClientSecret(null);
  };

  const handleManageBilling = async () => {
    if (!currentAgency) return;

    setActionLoading('portal');
    try {
      const { url } = await createBillingPortalSession(
        currentAgency.id,
        `${window.location.origin}/billing`
      );
      window.location.href = url;
    } catch (err: any) {
      setError(err.message || 'Failed to open billing portal');
    } finally {
      setActionLoading(null);
    }
  };

  if (agencyLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!currentAgency) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-semibold text-gray-900">No Agency Selected</h3>
        <p className="mt-2 text-sm text-gray-500">
          Select an agency from the dropdown above.
        </p>
      </div>
    );
  }

  // Check for success/cancel query params
  const urlParams = new URLSearchParams(window.location.search);
  const showSuccess = urlParams.get('success') === 'true';
  const showCanceled = urlParams.get('canceled') === 'true';

  const hasActiveSubscription =
    subscription?.status === 'active' || subscription?.status === 'trialing';

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Billing & Subscription</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage your subscription and view usage-based charges
        </p>
      </div>

      {showSuccess && (
        <div className="mb-6 rounded-md bg-green-50 p-4">
          <div className="flex">
            <CheckCircleIcon className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">
                Payment successful! Your subscription has been activated.
              </p>
            </div>
          </div>
        </div>
      )}

      {showCanceled && (
        <div className="mb-6 rounded-md bg-yellow-50 p-4">
          <div className="flex">
            <XCircleIcon className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <p className="text-sm font-medium text-yellow-800">
                Checkout was canceled. You can try again when you're ready.
              </p>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Subscription Card */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                {hasActiveSubscription ? 'Your Subscription' : 'Get Started'}
              </h3>

              <div className="mt-4">
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-gray-900">
                    {formatPrice(AGENCY_PLAN.priceCents)}
                  </span>
                  <span className="text-lg text-gray-500">/month</span>
                </div>

                {hasActiveSubscription && (
                  <div className="mt-3 flex items-center gap-3">
                    {getStatusBadge(subscription!.status)}
                    {subscription?.currentPeriodEnd && (
                      <span className="text-sm text-gray-500">
                        Renews {formatDate(subscription.currentPeriodEnd)}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <ul className="mt-6 space-y-3">
                {AGENCY_PLAN.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              <div className="mt-6 space-y-3">
                {hasActiveSubscription ? (
                  <button
                    type="button"
                    onClick={handleManageBilling}
                    disabled={actionLoading === 'portal'}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {actionLoading === 'portal' ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900" />
                    ) : (
                      <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                    )}
                    Manage Billing
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSubscribe}
                    disabled={actionLoading === 'subscribe'}
                    className="w-full rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 disabled:opacity-50"
                  >
                    {actionLoading === 'subscribe' ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        Processing...
                      </div>
                    ) : (
                      'Subscribe Now'
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Usage-Based Pricing Info */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center gap-2">
                <ChartBarIcon className="h-5 w-5 text-primary-600" />
                <h3 className="text-lg font-medium leading-6 text-gray-900">
                  Usage-Based Pricing
                </h3>
              </div>

              <p className="mt-3 text-sm text-gray-600">
                In addition to your monthly subscription, you pay for actual usage of AI voice
                services based on call duration.
              </p>

              <div className="mt-6 rounded-lg bg-gray-50 p-4">
                <h4 className="text-sm font-medium text-gray-900">How it works:</h4>
                <ul className="mt-3 space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-primary-600">1.</span>
                    Your clients make and receive calls using AI voice
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-primary-600">2.</span>
                    Usage is tracked per minute of call time
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-primary-600">3.</span>
                    Usage charges are billed at the end of each billing cycle
                  </li>
                </ul>
              </div>

              <div className="mt-6">
                <a
                  href="/usage"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-500"
                >
                  View your usage dashboard
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>

          {/* Payment Info Notice */}
          <div className="lg:col-span-2 rounded-lg bg-gray-50 p-4">
            <div className="flex">
              <CreditCardIcon className="h-5 w-5 text-gray-400" />
              <div className="ml-3">
                <p className="text-sm text-gray-600">
                  Payments are processed securely through Stripe. You can update your payment
                  method, view invoices, or cancel your subscription from the billing portal.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Embedded Checkout Modal */}
      {clientSecret && (
        <EmbeddedCheckoutModal
          isOpen={checkoutOpen}
          onClose={handleCheckoutClose}
          clientSecret={clientSecret}
          title="Complete Your Subscription"
        />
      )}
    </div>
  );
}
